import { v4 as uuidv4 } from "uuid";

import {
  MINI_NODE_INITIAL_SPACING,
  NODE_WIDTH,
} from "src/pages/journeys/constants";
import type {
  ExecutableJourneyNode,
  JourneyNodeDetails,
} from "src/pages/journeys/types";
import {
  type EntryAudienceExperimentBranchConfig,
  type EntryAudienceExperimentSplitGroup,
  JourneyNodeType,
} from "src/types/journeys";

/**
 * Builds one EntryAudienceExperimentBranch node per split group, laid out in a
 * centered row below the entry node (same spacing as getInitialBranchNodes).
 */
export const buildEntryAudienceExperimentBranchNodes = (
  splitGroups: EntryAudienceExperimentSplitGroup[],
  parentNodePosition: { x: number; y: number },
): ExecutableJourneyNode[] => {
  const startX =
    parentNodePosition.x - ((splitGroups.length - 1) * NODE_WIDTH) / 2;

  return splitGroups.map((group, index) => {
    const id = uuidv4();
    return {
      id,
      type: JourneyNodeType.EntryAudienceExperimentBranch,
      data: {
        id,
        name: group.name,
        description: null,
        segment_id: null,
        event_relationship_id: null,
        number_users: null,
        number_unique_users: null,
        sync_configs: null,
        message_config: null,
        config: {
          type: JourneyNodeType.EntryAudienceExperimentBranch,
          groupId: group.id,
          groupValue: group.value,
          isHoldout: group.isHoldout,
        },
      },
      position: {
        x: startX + index * NODE_WIDTH,
        y: parentNodePosition.y + MINI_NODE_INITIAL_SPACING,
      },
    };
  });
};

export type EntryAudienceExperimentBranchDiff = {
  /** Branch node ids whose split group no longer exists on the audience. */
  branchIdsToRemove: string[];
  /** Split groups with no corresponding branch node yet. */
  groupsToAdd: EntryAudienceExperimentSplitGroup[];
  /** Existing branch nodes whose group was renamed or re-valued. */
  branchesToUpdate: {
    branchId: string;
    group: EntryAudienceExperimentSplitGroup;
  }[];
};

/**
 * Diffs the entry node's current branch nodes against the configured split
 * groups (matched by audience_splits id) so branch nodes can be kept — and
 * their downstream connections preserved — across audience edits and re-saves.
 */
export const diffEntryAudienceExperimentBranches = (
  currentBranches: {
    id: string;
    data: JourneyNodeDetails<EntryAudienceExperimentBranchConfig>;
  }[],
  splitGroups: EntryAudienceExperimentSplitGroup[],
): EntryAudienceExperimentBranchDiff => {
  const groupsById = new Map(splitGroups.map((group) => [group.id, group]));
  const branchesByGroupId = new Map(
    currentBranches.map((branch) => [branch.data.config.groupId, branch]),
  );

  const branchIdsToRemove = currentBranches
    .filter((branch) => !groupsById.has(branch.data.config.groupId))
    .map((branch) => branch.id);

  const groupsToAdd = splitGroups.filter(
    (group) => !branchesByGroupId.has(group.id),
  );

  const branchesToUpdate: EntryAudienceExperimentBranchDiff["branchesToUpdate"] =
    [];
  for (const branch of currentBranches) {
    const group = groupsById.get(branch.data.config.groupId);
    if (!group) continue;

    const { config } = branch.data;
    if (
      branch.data.name !== group.name ||
      config.groupValue !== group.value ||
      config.isHoldout !== group.isHoldout
    ) {
      branchesToUpdate.push({ branchId: branch.id, group });
    }
  }

  return { branchIdsToRemove, groupsToAdd, branchesToUpdate };
};
